import React, { useState, useEffect } from 'react';
import { makeApiRequest, API_ENDPOINTS } from '../config/api';
import './Contact.css';

interface ContactContent {
  hero?: {
    title: string;
    subtitle: string;
    buttonText?: string;
  };
  intro?: string;
  partnership?: {
    title: string;
    description: string;
  };
  features?: Array<{
    title: string;
    description: string | {
      Email?: string;
      Phone?: string;
    };
  }>;
  contact_info?: {
    phone: string;
    email: string;

    address?: string;
  };
  form_fields?: Array<{
    name: string;
    label: string;
    type: string;
    required: boolean;
  }>;
}

interface PageData {
  page: {
  id: string;
  title: string;
  content: ContactContent;
  meta_description: string;
    updated_at: string;
    created_at: string;
    name: string;
    updated_by: string;
    images: any;
  };
}

const Contact: React.FC = () => {
  const [pageData, setPageData] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    company: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        const response = await makeApiRequest(API_ENDPOINTS.PAGES_BY_ID('contact'));
        if (response.ok) {
          const data = await response.json();
          setPageData(data);
        } else {
          console.error('Failed to fetch contact page data');
        }
      } catch (err) {
        console.error('Error fetching contact page data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    try {
      const response = await makeApiRequest(API_ENDPOINTS.CONTACT, {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setSubmitStatus('error');
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="loading-spinner"></div><p>Loading...</p></div>;
  }

  if (!pageData) {
    return <div className="error-container"><h2>Error Loading Page</h2></div>;
  }

  const { content } = pageData.page || pageData;

  // Parse phone numbers from the phone string - extract all numbers starting with +
  const phoneNumbers = content.contact_info?.phone
    ? content.contact_info.phone.match(/\+\d+[\s\d]*/g)?.map(p => p.trim()).filter(p => p.length > 0) || []
    : [];

  return (
    <div className="contact-page">
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">
              <div className="contact-form">
              <h2>Send Us a Message</h2>
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                  <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    disabled={submitStatus === 'submitting'}
                    />
                  </div>
                
                  <div className="form-group">
                  <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    disabled={submitStatus === 'submitting'}
                    />
                  </div>
                
                  <div className="form-group">
                    <label htmlFor="subject">Subject *</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                    disabled={submitStatus === 'submitting'}
                    />
                  </div>
                
                  <div className="form-group">
                    <label htmlFor="company">Company</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                    disabled={submitStatus === 'submitting'}
                    />
                  </div>
                
                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                    rows={6}
                      required
                    disabled={submitStatus === 'submitting'}
                  />
                  </div>
                
                  <button 
                    type="submit" 
                  className="btn"
                    disabled={submitStatus === 'submitting'}
                  >
                    {submitStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                  </button>
                
                  {submitStatus === 'success' && (
                  <div className="success-message">
                    Thank you for your message! We'll get back to you soon.
                  </div>
                  )}
                
                  {submitStatus === 'error' && (
                  <div className="error-message">
                    There was an error sending your message. Please try again.
                  </div>
                  )}
                </form>
              </div>

              {content.contact_info && (
                <div className="contact-info-section">
                  <h2>Contact Information</h2>

                  {phoneNumbers.length > 0 && (
                    <div className="contact-info-item">
                      <h3>Phone Numbers</h3>
                      <p className="phone-numbers-list">
                        {phoneNumbers.map((phone, index) => (
                          <React.Fragment key={index}>
                            <a href={`tel:${phone.replace(/\s+/g, '')}`}>{phone}</a>
                            {index < phoneNumbers.length - 1 && <span className="phone-separator"> | </span>}
                          </React.Fragment>
                        ))}
                      </p>
                    </div>
                  )}

                  {content.contact_info.email && (
                    <div className="contact-info-item">
                      <h3>Email</h3>
                      <p>
                        <a href={`mailto:${content.contact_info.email}`}>
                          {content.contact_info.email}
                        </a>
                      </p>
                    </div>
                  )}

                  {content.contact_info.address && (
                    <div className="contact-info-item">
                      <h3>Address</h3>
                      <p>{content.contact_info.address}</p>
                    </div>
                  )}
                </div>
              )}

              {content.partnership && (
                <div className="partnership-section">
                  <h2>{content.partnership.title}</h2>
                  <p>{content.partnership.description}</p>
                </div>
              )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 